import { sessionQueryVersionAtom } from '@/atoms/sessionAtoms';
import { sessionListSearchValueAtom } from '@/atoms/sideNavAtoms';
import { getSidebarPanelOverlayTriggerProps } from '@/components/layout/Navigation/sidebar-panel/sidebar-overlay-constants';
import { OfflineGatedTooltip } from '@/components/offline-recording/offline-gated-tooltip';
import Loader from '@/components/ui/loader';
import {
  MobileDrawerHeader,
  MobileFilterRow,
} from '@/components/ui/mobile-filter-drawer';
import { sessionStatusChipConfig } from '@/components/ui/session-status/session-status-chip';
import { statusIconVariants } from '@/components/ui/session-status/status.variants';
import { useVisibleSessionStatuses } from '@/components/ui/session-status/use-visible-session-statuses';
import { sessionUrgencyMessages } from '@/components/ui/session-urgency/messages';
import { logCopilotAction } from '@/features/copilot/action-log';
import {
  COPILOT_PRECHART_REQUEST_TTL_MS,
  copilotPrechartBatchAtom,
  copilotPrechartRequestAtom,
} from '@/features/copilot/prechart-atoms';
import {
  useIsAmbientCopilotEnabled,
  useIsNewSessionListEnabled,
  useIsSessionUrgencyEnabled,
} from '@/features/flags';
import {
  IS_CONSOLIDATED_SESSIONS_LIST_ENABLED,
  IS_SIDEBAR_PANEL_ENABLED,
} from '@/features/flags/legacy-flags';
import { useSettingsModal } from '@/features/settings/hooks/useSettingsModal';
import { useIntegrationsMarketplaceVisibilityGate } from '@/features/settings/integrations/integrations-marketplace-visibility-gate';
import {
  ability,
  ACTIONS,
  hasUpdatedAbilityAtom,
  PERMISSION_SUBJECTS,
} from '@/helpers/casl/ability';
import { useOfflineGating } from '@/hooks/desktop/useOfflineGating';
import { useHasActiveEhrIntegration } from '@/hooks/integrations/useHasActiveEhrIntegration';
import { useQueryPatientProfiles } from '@/hooks/query/useQueryPatientProfiles';
import useQueryUserTier from '@/hooks/query/useQueryUserTier';
import { useCanViewOthersSessions } from '@/hooks/useCanViewOthersSessions';
import { useIsDirectSessionSharingAllowed } from '@/hooks/useIsDirectSessionSharingAllowed';
import { useRBACPermissionCheck } from '@/hooks/useRBACPermissionCheck';
import { useSessionDateDisplay } from '@/hooks/useSessionDateDisplay';
import { useShowClinicianSelector } from '@/hooks/useShowClinicianSelector';
import { useTeamClinicianList } from '@/hooks/useTeamClinicianList';
import { track } from '@/lib/analytics/track';
import { parseTimeStamp } from '@/lib/parseTimeStamp';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import {
  trackScheduleUploadCompleted,
  trackScheduleUploadProcessingFailed,
  trackScheduleUploadSessionsCreated,
} from '@/models/analytics/features/trackSessionScheduleAnalytics';
import { isOnPrem } from '@/utils/buildProfile';
import { enumToDescription } from '@/utils/enumToDescription';
import { useUserTier } from '@heidi/commerce/data';
import { type SessionListHost } from '@heidi/session';
import { getDefaultStore, useAtomValue, useSetAtom } from 'jotai';

import { useSessionListVersion } from './useSessionListVersion';

/** The CASL ability has been written for this user. */
export function useHasUpdatedAbility(): boolean {
  return useAtomValue(hasUpdatedAbilityAtom);
}

/** The copilot pre-chart request, read inside React. */
const useCopilotPrechartRequest = () => useAtomValue(copilotPrechartRequestAtom);

/** The setter for the batch the orchestrator drains. */
const useSetCopilotPrechartBatch = () => useSetAtom(copilotPrechartBatchAtom);

/** Search box value. The atom stays app-side; filters read it through the host. */
export const useSessionListSearchValue = () => useAtomValue(sessionListSearchValueAtom);

/** Search box setter, paired with the read above. */
export const useSetSessionListSearchValue = () => useSetAtom(sessionListSearchValueAtom);

/** Which sessions query the legacy filter dropdown is driving. */
export const useSessionQueryVersion = () => useAtomValue(sessionQueryVersionAtom);

/** Pins the legacy dropdown onto the v1 query while it is open. */
export const useSetSessionQueryVersion = () => useSetAtom(sessionQueryVersionAtom);

/**
 * One object, created once. Callers pass this reference; it is not rebuilt
 * per render, so it stays out of the first-load graph.
 */
export const sessionListHost: SessionListHost = {
  identity: {
    ability,
    ACTIONS,
    PERMISSION_SUBJECTS,
    useRBACPermissionCheck,
    useShowClinicianSelector,
    useTeamClinicianList,
    useQueryUserTier,
    useUserTier,
    useHasUpdatedAbility,
    useCanViewOthersSessions,
    useIsDirectSessionSharingAllowed,
  },
  integrations: {
    useHasActiveEhrIntegration,
    useIntegrationsMarketplaceVisibilityGate,
  },
  shell: {
    isOnPrem,
    useIsNewSessionListEnabled,
    useIsSessionUrgencyEnabled,
    IS_SIDEBAR_PANEL_ENABLED,
    IS_CONSOLIDATED_SESSIONS_LIST_ENABLED,
    getSidebarPanelOverlayTriggerProps,
    useSessionListSearchValue,
    useSetSessionListSearchValue,
    Loader,
    MobileDrawerHeader,
    MobileFilterRow,
    parseTimeStamp,
    useSettingsModal,
  },
  sessionApp: {
    useSessionDateDisplay,
    useSessionListVersion,
    useSessionQueryVersion,
    useSetSessionQueryVersion,
    useVisibleSessionStatuses,
    sessionStatusChipConfig,
    statusIconVariants,
    sessionUrgencyMessages,
    sessionStatusDescription: enumToDescription,
  },
  analytics: {
    track: (event) => {
      if (event.event_name === 'sessions_filter_applied') {
        track({
          event_name: TrackingEventName.SessionsFilterApplied,
          props: event.props,
          options: event.options,
        });
        return;
      }
      track({
        event_name: TrackingEventName.ScheduleUploadStarted,
        props: event.props,
        options: event.options,
      });
    },
    TrackingEventName: {
      ScheduleUploadStarted: TrackingEventName.ScheduleUploadStarted,
      SessionsFilterApplied: TrackingEventName.SessionsFilterApplied,
    },
    trackScheduleUploadCompleted,
    trackScheduleUploadProcessingFailed,
    trackScheduleUploadSessionsCreated,
  },
  foreignApp: {
    useIsAmbientCopilotEnabled,
    logCopilotAction,
    COPILOT_PRECHART_REQUEST_TTL_MS,
    useCopilotPrechartRequest,
    useSetCopilotPrechartBatch,
    getCopilotPrechartRequest: () => getDefaultStore().get(copilotPrechartRequestAtom),
    setCopilotPrechartRequest: (request) => {
      getDefaultStore().set(copilotPrechartRequestAtom, request);
    },
  },
  desktop: {
    useOfflineGating,
    OfflineGatedTooltip,
  },
  patients: {
    useQueryPatientProfiles,
  },
};
